/**
 * Admin Client Detail Page — edit client, enrollment settings, view users and projects
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../services/api';
import type { AutoApproveMode } from '../../../shared/types/auth';
import { formatProjectName } from '../../../shared/formatProjectName';
import { CreateUserModal } from '../../components/admin/CreateUserModal';

const CLIENT_ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'viewer', label: 'General Viewer' },
  { value: 'commenter', label: 'User / Commenter' },
  { value: 'people_manager', label: 'People Manager' },
  { value: 'client_admin', label: 'Client Admin' },
];
const INTERNAL_ROLES = ['admin', 'operations', 'sales'];

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);

  // Enrollment state
  const [enrollment, setEnrollment] = useState<any>(null);
  const [enrollmentForm, setEnrollmentForm] = useState({
    enrollmentEnabled: false,
    autoApproveMode: 'manual' as AutoApproveMode,
    autoApproveDomains: '',
    memberLimit: '',
  });
  const [enrollmentSaving, setEnrollmentSaving] = useState(false);
  const [pendingUsers, setPendingUsers] = useState<any[]>([]);
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [assigningProject, setAssigningProject] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Members state
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [allClients, setAllClients] = useState<any[]>([]);
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState('viewer');
  const [memberBusy, setMemberBusy] = useState(false);
  const [memberError, setMemberError] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [data, enrollData, pending, projects, usersList, clientsList] = await Promise.all([
        api.admin.getClient(id),
        api.admin.getEnrollmentSettings(id),
        api.admin.getPendingUsers(id),
        api.project.getAll(),
        api.admin.getUsers(),
        api.admin.getClients(),
      ]);
      setClient(data);
      setAllProjects(projects);
      setAllUsers(usersList);
      setAllClients(clientsList);
      setForm({
        name: data.name,
        contactEmail: data.contactEmail || '',
        contactName: data.contactName || '',
        notes: data.notes || '',
      });
      setEnrollment(enrollData);
      setEnrollmentForm({
        enrollmentEnabled: enrollData.enrollmentEnabled,
        autoApproveMode: enrollData.autoApproveMode,
        autoApproveDomains: enrollData.autoApproveDomains || '',
        memberLimit: enrollData.memberLimit?.toString() || '',
      });
      setPendingUsers(pending);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await api.admin.updateClient(id, form);
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const saveEnrollment = async () => {
    if (!id) return;
    setEnrollmentSaving(true);
    try {
      await api.admin.updateEnrollmentSettings(id, {
        enrollmentEnabled: enrollmentForm.enrollmentEnabled,
        autoApproveMode: enrollmentForm.autoApproveMode,
        autoApproveDomains: enrollmentForm.autoApproveDomains || null,
        memberLimit: enrollmentForm.memberLimit ? parseInt(enrollmentForm.memberLimit) : null,
      });
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setEnrollmentSaving(false);
    }
  };

  const regenerateCode = async () => {
    if (!id) return;
    try {
      const result = await api.admin.regenerateEnrollmentCode(id);
      setEnrollment((prev: any) => ({ ...prev, enrollmentCode: result.enrollmentCode }));
    } catch (err) {
      console.error(err);
    }
  };

  const approveUser = async (userId: string) => {
    if (!id) return;
    try {
      await api.admin.approveUser(id, userId);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const rejectUser = async (userId: string) => {
    if (!id) return;
    try {
      await api.admin.rejectUser(id, userId);
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const copyCode = () => {
    if (enrollment?.enrollmentCode) {
      navigator.clipboard.writeText(enrollment.enrollmentCode);
    }
  };

  const assignProject = async (projectId: string) => {
    if (!id || !projectId) return;
    setAssigningProject(true);
    try {
      await api.admin.assignClient(projectId, id);
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setAssigningProject(false);
    }
  };

  const unassignProject = async (projectId: string) => {
    setAssigningProject(true);
    try {
      await api.admin.assignClient(projectId, null);
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setAssigningProject(false);
    }
  };

  // ───── Members ─────
  const addMember = async () => {
    if (!id || !addUserId) return;
    setMemberBusy(true);
    setMemberError('');
    try {
      await api.admin.addClientMember(id, addUserId, addRole);
      setAddUserId('');
      setAddRole('viewer');
      await load();
    } catch (err: any) {
      setMemberError(err.message || 'Failed to add member');
    } finally {
      setMemberBusy(false);
    }
  };

  const changeMemberRole = async (userId: string, role: string) => {
    if (!id) return;
    setMemberError('');
    try {
      await api.admin.updateClientMemberRole(id, userId, role);
      await load();
    } catch (err: any) {
      setMemberError(err.message || 'Failed to change role');
    }
  };

  const removeMember = async (userId: string, label: string) => {
    if (!id) return;
    if (!confirm(`Remove ${label} from ${client?.name}? Their account is kept and will show under Unassigned users.`)) return;
    setMemberError('');
    try {
      await api.admin.removeClientMember(id, userId);
      await load();
    } catch (err: any) {
      setMemberError(err.message || 'Failed to remove member');
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const dataUrl = reader.result as string;
          await api.admin.uploadClientLogo(id, dataUrl);
          load();
        } catch (err) {
          console.error('Logo upload failed:', err);
        } finally {
          setUploadingLogo(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error(err);
      setUploadingLogo(false);
    }
    // Reset input so re-selecting same file triggers change
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleLogoDelete = async () => {
    if (!id) return;
    setUploadingLogo(true);
    try {
      await api.admin.deleteClientLogo(id);
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) return <div style={{ color: '#94a3b8' }}>Loading...</div>;
  if (!client) return <div style={{ color: '#ef4444' }}>Client not found</div>;

  return (
    <div>
      <Link to="/admin/clients" style={{ color: '#64748b', textDecoration: 'none', fontSize: 13 }}>
        &larr; Clients
      </Link>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: '8px 0 24px' }}>{client.name}</h1>

      {/* Edit form */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Details</h2>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
          <div style={styles.logoBox}>
            {client.logoPath ? (
              <img src={client.logoPath} alt={`${client.name} logo`} style={styles.logoImg} />
            ) : (
              <span style={{ color: '#475569', fontSize: 11 }}>No logo</span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={styles.label}>Client Logo</label>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => logoInputRef.current?.click()}
                style={styles.smallBtn}
                disabled={uploadingLogo}
              >
                {uploadingLogo ? 'Uploading...' : client.logoPath ? 'Change Logo' : 'Upload Logo'}
              </button>
              {client.logoPath && (
                <button onClick={handleLogoDelete} style={styles.rejectBtn} disabled={uploadingLogo}>
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <div style={styles.formGrid}>
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Contact Email" value={form.contactEmail} onChange={(v) => setForm({ ...form, contactEmail: v })} />
          <Field label="Contact Name" value={form.contactName} onChange={(v) => setForm({ ...form, contactName: v })} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={styles.label}>Notes</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            style={{ ...styles.input, minHeight: 60, resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
          />
        </div>
        <button onClick={save} style={styles.saveBtn} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </section>

      {/* Enrollment Settings */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Enrollment</h2>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={enrollmentForm.enrollmentEnabled}
              onChange={(e) => setEnrollmentForm({ ...enrollmentForm, enrollmentEnabled: e.target.checked })}
              style={{ accentColor: '#4F7CFF' }}
            />
            <span style={{ color: '#f1f5f9', fontSize: 14 }}>Enable self-enrollment</span>
          </label>
        </div>

        {enrollment?.enrollmentCode && (
          <div style={{ marginBottom: 16 }}>
            <label style={styles.label}>Enrollment Code</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <code style={styles.code}>{enrollment.enrollmentCode}</code>
              <button onClick={copyCode} style={styles.smallBtn}>Copy</button>
              <button onClick={regenerateCode} style={{ ...styles.smallBtn, background: '#374151' }}>Regenerate</button>
            </div>
          </div>
        )}

        <div style={styles.formGrid}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={styles.label}>Auto-Approve Mode</label>
            <select
              value={enrollmentForm.autoApproveMode}
              onChange={(e) => setEnrollmentForm({ ...enrollmentForm, autoApproveMode: e.target.value as AutoApproveMode })}
              style={styles.input}
            >
              <option value="manual">Manual Review</option>
              <option value="enrollment_code">Auto-approve with valid code</option>
              <option value="email_domain">Auto-approve matching email domain</option>
              <option value="both">Require both code + matching domain</option>
            </select>
          </div>

          <Field
            label="Member Limit"
            value={enrollmentForm.memberLimit}
            onChange={(v) => setEnrollmentForm({ ...enrollmentForm, memberLimit: v })}
            placeholder="Unlimited"
          />
        </div>

        {(enrollmentForm.autoApproveMode === 'email_domain' || enrollmentForm.autoApproveMode === 'both') && (
          <div style={{ marginBottom: 16 }}>
            <label style={styles.label}>Approved Email Domains (comma-separated)</label>
            <input
              type="text"
              value={enrollmentForm.autoApproveDomains}
              onChange={(e) => setEnrollmentForm({ ...enrollmentForm, autoApproveDomains: e.target.value })}
              style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
              placeholder="acme.com, acme.org"
            />
          </div>
        )}

        <button onClick={saveEnrollment} style={styles.saveBtn} disabled={enrollmentSaving}>
          {enrollmentSaving ? 'Saving...' : 'Save Enrollment Settings'}
        </button>
      </section>

      {/* Pending Users */}
      {pendingUsers.length > 0 && (
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Pending Users ({pendingUsers.length})</h2>
          {pendingUsers.map((u: any) => (
            <div key={u.userId} style={styles.listRow}>
              <div>
                <span style={{ color: '#f1f5f9', fontSize: 14 }}>{u.name || u.email}</span>
                {u.name && <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>{u.email}</span>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => approveUser(u.userId)} style={styles.approveBtn}>Approve</button>
                <button onClick={() => rejectUser(u.userId)} style={styles.rejectBtn}>Reject</button>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Members */}
      <section style={styles.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <h2 style={{ ...styles.sectionTitle, margin: 0 }}>Members ({client.users?.length || 0})</h2>
          <button onClick={() => setShowCreateUser(true)} style={styles.smallBtn}>+ New User</button>
        </div>

        {memberError && <div style={styles.errorBanner}>{memberError}</div>}

        {client.users?.map((u: any) => (
          <div key={u.id} style={{ ...styles.listRow, gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <span style={{ color: '#f1f5f9', fontSize: 14 }}>{u.name || u.email}</span>
              {u.name && <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>{u.email}</span>}
              {u.membershipStatus && u.membershipStatus !== 'active' && (
                <span style={{ ...styles.pill, marginLeft: 8, color: '#f59e0b', background: 'rgba(245,158,11,0.15)' }}>{u.membershipStatus}</span>
              )}
              {u.status && u.status !== 'active' && (
                <span style={{ ...styles.pill, marginLeft: 8, color: '#ef4444', background: 'rgba(239,68,68,0.15)' }}>account {u.status}</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select
                value={u.clientRole || 'viewer'}
                onChange={(e) => changeMemberRole(u.id, e.target.value)}
                style={{ ...styles.input, padding: '6px 10px', fontSize: 12 }}
              >
                {CLIENT_ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button onClick={() => removeMember(u.id, u.name || u.email)} style={styles.rejectBtn}>Remove</button>
            </div>
          </div>
        ))}
        {(!client.users || client.users.length === 0) && (
          <div style={{ color: '#64748b', fontSize: 14, marginBottom: 12 }}>No members yet</div>
        )}

        {(() => {
          const memberIds = new Set((client.users || []).map((u: any) => u.id));
          const candidates = allUsers.filter((u) => !memberIds.has(u.id) && !INTERNAL_ROLES.includes(u.role));
          const unassignedFirst = [...candidates].sort((a, b) => {
            const ua = (a.clients?.length || 0) === 0 ? 0 : 1;
            const ub = (b.clients?.length || 0) === 0 ? 0 : 1;
            return ua - ub || String(a.name || a.email).localeCompare(String(b.name || b.email));
          });
          return (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
              <select
                value={addUserId}
                onChange={(e) => setAddUserId(e.target.value)}
                style={{ ...styles.input, flex: 1, minWidth: 220, maxWidth: 360 }}
                disabled={memberBusy}
              >
                <option value="">Add an existing user…</option>
                {unassignedFirst.map((u) => (
                  <option key={u.id} value={u.id}>
                    {(u.name ? `${u.name} — ` : '') + u.email}{(u.clients?.length || 0) === 0 ? '  (unassigned)' : `  (${u.clients.map((c: any) => c.clientName).join(', ')})`}
                  </option>
                ))}
              </select>
              <select value={addRole} onChange={(e) => setAddRole(e.target.value)} style={styles.input} disabled={memberBusy || !addUserId}>
                {CLIENT_ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button onClick={addMember} style={{ ...styles.approveBtn, opacity: addUserId ? 1 : 0.4 }} disabled={memberBusy || !addUserId}>
                {memberBusy ? 'Adding…' : 'Add'}
              </button>
            </div>
          );
        })()}
      </section>

      {showCreateUser && (
        <CreateUserModal
          mode="client"
          clients={allClients}
          defaultClientId={id}
          lockClient
          onClose={() => setShowCreateUser(false)}
          onCreated={load}
        />
      )}

      {/* Projects */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Projects ({client.projects?.length || 0})</h2>
        {client.projects?.map((p: any) => (
          <div key={p.id} style={styles.listRow}>
            <Link to={`/admin/projects/${p.id}`} style={{ color: '#f1f5f9', fontSize: 14, textDecoration: 'none', flex: 1 }}>
              {formatProjectName(p)}
            </Link>
            <span style={{ color: '#64748b', fontSize: 12, marginRight: 12 }}>{p.category || '—'}</span>
            <button onClick={() => unassignProject(p.id)} style={styles.rejectBtn} disabled={assigningProject}>
              Remove
            </button>
          </div>
        ))}
        {(!client.projects || client.projects.length === 0) && (
          <div style={{ color: '#64748b', fontSize: 14, marginBottom: 12 }}>No projects assigned</div>
        )}

        {(() => {
          const assignedIds = new Set((client.projects || []).map((p: any) => p.id));
          const unassigned = allProjects.filter((p: any) => !assignedIds.has(p.id) && !p.clientId);
          if (unassigned.length === 0) return null;
          return (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
              <select
                id="assign-project-select"
                style={{ ...styles.input, flex: 1, maxWidth: 300 }}
                defaultValue=""
                disabled={assigningProject}
              >
                <option value="" disabled>Select a project to assign...</option>
                {unassigned.map((p: any) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <button
                onClick={() => {
                  const select = document.getElementById('assign-project-select') as HTMLSelectElement;
                  if (select?.value) assignProject(select.value);
                }}
                style={styles.approveBtn}
                disabled={assigningProject}
              >
                {assigningProject ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          );
        })()}
      </section>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={styles.label}>{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} style={styles.input} placeholder={placeholder} />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    background: '#1a1a1a', border: '1px solid #333333', borderRadius: 12, padding: 24, marginBottom: 20,
  },
  logoBox: {
    width: 100, height: 100, borderRadius: 8, border: '1px solid #333333', background: '#0d0d0d',
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
  },
  logoImg: {
    maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' as const,
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#f1f5f9', margin: '0 0 16px' },
  formGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginBottom: 16,
  },
  label: { color: '#94a3b8', fontSize: 12, fontWeight: 500 },
  input: {
    background: '#0d0d0d', border: '1px solid #333333', borderRadius: 6,
    padding: '8px 12px', color: '#f1f5f9', fontSize: 13, outline: 'none',
  },
  saveBtn: {
    background: '#4F7CFF', color: '#fff', border: 'none', borderRadius: 8,
    padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  smallBtn: {
    background: '#4F7CFF', color: '#fff', border: 'none', borderRadius: 6,
    padding: '6px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
  },
  code: {
    background: '#0d0d0d', border: '1px solid #333333', borderRadius: 6,
    padding: '8px 16px', color: '#6B95FF', fontSize: 16, fontFamily: 'monospace',
    letterSpacing: '0.15em',
  },
  listRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 0', borderBottom: '1px solid #333333', textDecoration: 'none',
  },
  approveBtn: {
    background: '#166534', color: '#86efac', border: 'none', borderRadius: 6,
    padding: '6px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
  },
  rejectBtn: {
    background: '#7f1d1d', color: '#fca5a5', border: 'none', borderRadius: 6,
    padding: '6px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
  },
  pill: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
  },
  errorBanner: {
    background: '#7f1d1d', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12,
  },
};
